import {
  applySlashCommand,
  conflictPointResponseSchema,
  errorResponseSchema,
  formatCoordinate,
  matchCommands,
  processPointResponseSchema,
  slashMenuActive,
  toggleTodo,
  attachmentCommitResultSchema,
  uploadTicketSchema,
  type PointDto,
  type SlashCommand,
  type WaypointDto,
} from '@datum/shared'
import {
  Eye,
  Gauge,
  Heading1,
  Heading2,
  Image,
  ListChecks,
  Mic,
  PencilLine,
  Sparkles,
  Square,
  TriangleAlert,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { adoptConflictServer, resolveConflict, type SyncState } from '../lib/sync'
import { NoteViewer } from './NoteViewer'
import { SlashMenu } from './SlashMenu'
import { SyncBadge } from './SyncBadge'

interface EditorPaneProps {
  point: PointDto | null
  waypoints: WaypointDto[]
  sync: SyncState
  /** App owns versioning/queueing: receives the user's edits against the given base point. */
  onSave: (point: PointDto, edits: { title: string; content_raw: string; waypoint_id: string | null }) => void
  /** Authoritative server row (AI result) — replaces the cached copy, no re-queue. */
  onServerPoint: (next: PointDto) => void
}

const AUTOSAVE_MS = 1_200
const RECORDING_MAX_MS = 120_000

export function EditorPane({ point, waypoints, sync, onSave, onServerPoint }: EditorPaneProps) {
  const [title, setTitle] = useState('')
  const [raw, setRaw] = useState('')
  const [waypointId, setWaypointId] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [caret, setCaret] = useState(0)
  const [menu, setMenu] = useState<{ query: string; activeIndex: number } | null>(null)
  const [message, setMessage] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null)
  const [recording, setRecording] = useState(false)
  const [aiBusy, setAiBusy] = useState(false)
  const [mode, setMode] = useState<'write' | 'read'>('write')

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const recordStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Load key = id + server version: flips automatically when a conflict
  // adopts the server row (updated_at/version change through the cache).
  const loadedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!point) {
      loadedKeyRef.current = null
      setTitle('')
      setRaw('')
      setWaypointId(null)
      setDirty(false)
      setMenu(null)
      return
    }
    const key = `${point.id}:${point.version}`
    if (loadedKeyRef.current === key) return
    loadedKeyRef.current = key
    setTitle(point.title)
    setRaw(point.content_raw)
    setWaypointId(point.waypoint_id)
    setDirty(false)
    setMenu(null)
  }, [point])

  const saveNow = useCallback(() => {
    if (!point) return
    onSave(point, { title, content_raw: raw, waypoint_id: waypointId })
    setDirty(false)
  }, [point, title, raw, waypointId, onSave])

  useEffect(() => {
    if (!point || !dirty) return
    const timer = setTimeout(saveNow, AUTOSAVE_MS)
    return () => clearTimeout(timer)
  }, [point, dirty, saveNow])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        saveNow()
        setMessage({ text: 'saved', tone: 'ok' })
        if (messageTimer.current !== null) clearTimeout(messageTimer.current)
        messageTimer.current = setTimeout(() => setMessage(null), 1_500)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [saveNow])

  const messageTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function flash(text: string, tone: 'ok' | 'warn' = 'ok'): void {
    setMessage({ text, tone })
    if (messageTimer.current !== null) clearTimeout(messageTimer.current)
    messageTimer.current = setTimeout(() => setMessage(null), 2_500)
  }

  function trackCaretAndMenu(value: string, nextCaret: number): void {
    setCaret(nextCaret)
    const token = slashMenuActive(value, nextCaret)
    setMenu(token ? { query: token.query, activeIndex: 0 } : null)
  }

  function onDraftChange(next: string): void {
    setRaw(next)
    setDirty(true)
    const el = textareaRef.current
    if (el) trackCaretAndMenu(next, el.selectionStart ?? next.length)
  }

  function insertTextAtCaret(text: string): void {
    const el = textareaRef.current
    const position = el?.selectionStart ?? caret
    const next = raw.slice(0, position) + text + raw.slice(position)
    setRaw(next)
    setDirty(true)
    const after = position + text.length
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(after, after)
      setCaret(after)
    })
  }

  function insertBlockAtLineStart(prefix: string): void {
    const position = textareaRef.current?.selectionStart ?? raw.length
    const lineStart = raw.lastIndexOf('\n', Math.max(0, position - 1)) + 1
    const next = raw.slice(0, lineStart) + prefix + raw.slice(lineStart)
    setRaw(next)
    setDirty(true)
    const after = lineStart + prefix.length
    requestAnimationFrame(() => {
      textareaRef.current?.focus()
      textareaRef.current?.setSelectionRange(after, after)
      setCaret(after)
    })
  }

  async function uploadAndInsert(kind: 'photo' | 'audio', blob: Blob, fileName: string): Promise<void> {
    if (!point) return
    try {
      const contentType = blob.type !== '' ? blob.type : kind === 'audio' ? 'audio/webm' : 'image/jpeg'
      const ticketResponse = await api.api.media['upload-url'].$post({
        json: {
          datum_point_id: point.id,
          file_name: fileName,
          content_type: contentType,
          size_bytes: blob.size,
        },
      })
      const ticketParsed = uploadTicketSchema.safeParse(await ticketResponse.json().catch(() => null))
      if (!ticketResponse.ok || !ticketParsed.success) {
        flash('upload ticket rejected', 'warn')
        return
      }
      const ticket = ticketParsed.data

      const put = await fetch(ticket.upload_url, {
        method: 'PUT',
        headers: { 'content-type': contentType },
        body: blob,
      })
      if (!put.ok) {
        flash('object upload failed', 'warn')
        return
      }

      const commitResponse = await api.api.media.attachments[':attachment_id'].commit.$post({
        param: { attachment_id: ticket.attachment_id },
      })
      const commitParsed = attachmentCommitResultSchema.safeParse(await commitResponse.json().catch(() => null))
      if (!commitResponse.ok || !commitParsed.success) {
        flash('attachment commit failed', 'warn')
        return
      }
      insertTextAtCaret(commitParsed.data.editor_token)
      flash(`${kind} attached`)
    } catch (error) {
      console.warn('[datum] upload flow failed', error)
      flash('upload failed — check connection', 'warn')
    }
  }

  /**
   * Server-side AI pass (Whisper + summary) on the *synced* version of this
   * note. Guarded on a clean sync state so the server processes what the user
   * can see; a 409 means someone/something advanced the row mid-flight and the
   * authoritative copy is adopted exactly like a push conflict.
   */
  async function runAiProcess(): Promise<void> {
    if (!point || aiBusy) return
    if (dirty) saveNow()
    if (dirty || sync.pending.includes(point.id)) {
      flash('not synced yet — summarize once the badge is emerald', 'warn')
      return
    }
    setAiBusy(true)
    try {
      const response = await api.api.ai['process-point'].$post({ json: { datum_point_id: point.id } })
      const payload = await response.json().catch(() => null)
      if (response.ok) {
        const parsed = processPointResponseSchema.safeParse(payload)
        if (!parsed.success) {
          flash('unexpected AI response', 'warn')
          return
        }
        onServerPoint(parsed.data.point)
        flash(parsed.data.transcribed_attachments > 0 ? `AI done — ${parsed.data.transcribed_attachments} audio transcribed` : 'AI summary ready')
        return
      }
      if (response.status === 409) {
        const conflict = conflictPointResponseSchema.safeParse(payload)
        if (conflict.success) adoptConflictServer(conflict.data.server)
        flash('changed mid-processing — server row adopted', 'warn')
        return
      }
      const err = errorResponseSchema.safeParse(payload)
      const code = err.success ? err.data.error.code : `http_${response.status}`
      if (code === 'ai_quota_exceeded') flash('daily AI limit reached — resets at midnight UTC', 'warn')
      else if (code === 'ai_disabled') flash('AI disabled by operator', 'warn')
      else if (code === 'not_found') flash('note not on server yet', 'warn')
      else flash(`AI failed (${code})`, 'warn')
    } catch {
      flash('AI request failed — check connection', 'warn')
    } finally {
      setAiBusy(false)
    }
  }

  function stopRecording(): void {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
  }

  async function toggleRecorder(): Promise<void> {
    if (recording) {
      stopRecording()
      return
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      flash('audio capture unsupported in this browser')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const chunks: Blob[] = []
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop())
        if (recordStopTimer.current !== null) clearTimeout(recordStopTimer.current)
        recordStopTimer.current = null
        setRecording(false)
        const blob = new Blob(chunks, { type: 'audio/webm' })
        if (blob.size > 0) {
          void uploadAndInsert('audio', blob, `field-voice-${new Date().toISOString().slice(0, 19)}.webm`)
        }
      }
      recorder.start()
      recorderRef.current = recorder
      setRecording(true)
      recordStopTimer.current = setTimeout(stopRecording, RECORDING_MAX_MS)
    } catch {
      flash('microphone unavailable')
    }
  }

  function pickCommand(command: SlashCommand): void {
    setMenu(null)
    if (command.kind === 'block') {
      const applied = applySlashCommand(raw, caret, command)
      if (applied) {
        setRaw(applied.value)
        setDirty(true)
        requestAnimationFrame(() => {
          textareaRef.current?.focus()
          textareaRef.current?.setSelectionRange(applied.caret, applied.caret)
          setCaret(applied.caret)
        })
      } else {
        insertBlockAtLineStart(command.insert ?? '')
      }
      return
    }
    if (command.action === 'open-photo') photoInputRef.current?.click()
    else void toggleRecorder()
  }

  function onEditorKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (!menu) return
    const commands = matchCommands(menu.query)
    if (commands.length === 0) return
    const currentIndex = ((menu.activeIndex % commands.length) + commands.length) % commands.length

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setMenu({ ...menu, activeIndex: currentIndex + 1 })
        break
      case 'ArrowUp':
        event.preventDefault()
        setMenu({ ...menu, activeIndex: currentIndex - 1 })
        break
      case 'Enter':
      case 'Tab': {
        event.preventDefault()
        const command = commands[currentIndex]
        if (command) pickCommand(command)
        break
      }
      case 'Escape':
        event.preventDefault()
        setMenu(null)
        break
      default:
        break
    }
  }

  function toggleNthTodo(index: number): void {
    const next = toggleTodo(raw, index)
    if (next !== null) onDraftChange(next)
  }

  if (!point) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <p className="text-sm text-zinc-500">No note selected.</p>
          <p className="mono mt-2 text-[11px] text-zinc-600">
            press <kbd className="rounded border border-edge bg-ink-800 px-1">N</kbd> for a new point at map center
          </p>
        </div>
      </div>
    )
  }

  const conflicted = sync.conflicts.includes(point.id)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {conflicted && (
        <div className="flex items-center justify-between gap-2 border-b border-conflict/30 bg-conflict/10 px-3 py-1.5">
          <span className="flex items-center gap-2 text-[12px] text-conflict">
            <TriangleAlert size={13} />
            Server version adopted — your edit was newer than the merge base.
          </span>
          <button
            type="button"
            onClick={() => resolveConflict(point.id)}
            className="mono rounded border border-conflict/30 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-conflict hover:bg-conflict/10"
          >
            ack
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-edge px-3 py-2">
        <input
          value={title}
          onChange={(event) => {
            setTitle(event.target.value)
            setDirty(true)
          }}
          placeholder="Note title"
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold tracking-tight text-zinc-50 outline-none placeholder:text-zinc-600"
          aria-label="Note title"
        />
        <SyncBadge pointId={point.id} />
      </div>

      {point.summary !== null && point.summary !== '' && (
        <div className="flex items-start gap-2 border-b border-edge bg-ink-850 px-3 py-1.5">
          <Sparkles size={12} className="mt-0.5 shrink-0 text-zinc-600" />
          <span className="text-[11px] leading-relaxed text-zinc-400">{point.summary}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1 border-b border-edge px-3 py-1.5">
        <ToolButton label="Heading 1" onClick={() => insertBlockAtLineStart('# ')}>
          <Heading1 size={14} />
        </ToolButton>
        <ToolButton label="Heading 2" onClick={() => insertBlockAtLineStart('## ')}>
          <Heading2 size={14} />
        </ToolButton>
        <ToolButton label="Todo" onClick={() => insertBlockAtLineStart('- [ ] ')}>
          <ListChecks size={14} />
        </ToolButton>
        <ToolButton label="Bench mark" onClick={() => insertBlockAtLineStart('::bench label = value unit')}>
          <Gauge size={14} />
        </ToolButton>
        <ToolButton label="Photo" onClick={() => photoInputRef.current?.click()}>
          <Image size={14} />
        </ToolButton>
        <ToolButton label={recording ? 'Stop recording' : 'Record audio'} onClick={() => void toggleRecorder()} active={recording}>
          {recording ? <Square size={14} className="text-conflict" /> : <Mic size={14} />}
        </ToolButton>
        <ToolButton
          label={mode === 'write' ? 'Preview rendered note' : 'Edit raw text'}
          onClick={() => setMode((current) => (current === 'write' ? 'read' : 'write'))}
          active={mode === 'read'}
        >
          {mode === 'write' ? <Eye size={14} /> : <PencilLine size={14} />}
        </ToolButton>
        <ToolButton
          label={aiBusy ? 'AI processing…' : 'Summarize with AI (transcribes audio first)'}
          onClick={() => void runAiProcess()}
        >
          <Sparkles size={14} className={aiBusy ? 'animate-pulse text-pending' : 'text-zinc-400'} />
        </ToolButton>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={waypointId ?? ''}
            onChange={(event) => {
              const next = event.target.value === '' ? null : event.target.value
              setWaypointId(next)
              setDirty(true)
            }}
            aria-label="Assign waypoint"
            className="mono max-w-40 rounded border border-edge bg-ink-850 px-2 py-1 text-[11px] text-zinc-300 outline-none focus:border-edge-strong"
          >
            <option value="">no waypoint</option>
            {waypoints.map((waypoint) => (
              <option key={waypoint.id} value={waypoint.id}>
                {waypoint.name}
                {waypoint.floor_level ? ` · ${waypoint.floor_level}` : ''}
              </option>
            ))}
          </select>
        </div>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void uploadAndInsert('photo', file, file.name)
            event.target.value = ''
          }}
        />
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {menu && mode === 'write' && (
          <SlashMenu
            query={menu.query}
            activeIndex={menu.activeIndex}
            onPick={pickCommand}
            onHover={(index) => setMenu({ ...menu, activeIndex: index })}
          />
        )}
        {mode === 'write' ? (
          <textarea
            ref={textareaRef}
            value={raw}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onEditorKeyDown}
            onKeyUp={(event) => trackCaretAndMenu(event.currentTarget.value, event.currentTarget.selectionStart ?? 0)}
            onClick={(event) => trackCaretAndMenu(event.currentTarget.value, event.currentTarget.selectionStart ?? 0)}
            placeholder="/h1  /todo  /bench  /photo  /audio …"
            spellCheck={false}
            className="mono min-h-0 flex-1 resize-none bg-transparent px-3 py-3 text-[13px] leading-relaxed text-zinc-200 outline-none placeholder:text-zinc-700"
            aria-label="Note content editor"
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
            <NoteViewer contentRaw={raw} onToggleTodo={(index) => toggleNthTodo(index)} compact />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-edge px-3 py-1.5">
        <span className="mono truncate text-[10px] text-zinc-500">
          {formatCoordinate(point.latitude, point.longitude)}
          {point.altitude !== null ? ` · ${point.altitude.toFixed(0)}m` : ''}
          {point.accuracy !== null ? ` · ±${point.accuracy.toFixed(1)}m` : ''}
          <span className="text-zinc-600"> · v{point.version}</span>
        </span>
        <span className="mono shrink-0 text-[10px]">
          {message !== null ? (
            <span className={message.tone === 'warn' ? 'text-pending' : 'text-sync'}>{message.text}</span>
          ) : dirty ? (
            <span className="text-pending">unsaved…</span>
          ) : (
            <span className="text-zinc-600">⌘S save · / commands</span>
          )}
        </span>
      </div>
    </div>
  )
}

function ToolButton({
  label,
  onClick,
  active,
  children,
}: {
  label: string
  onClick: () => void
  active?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`rounded border p-1.5 transition-colors ${
        active
          ? 'border-conflict/40 bg-conflict/10 text-conflict'
          : 'border-edge text-zinc-400 hover:border-edge-strong hover:bg-ink-800 hover:text-zinc-200'
      }`}
    >
      {children}
    </button>
  )
}
